import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from '@/contexts/TranslationContext';

const goals = [
  {
    id: 1,
    type: 'Minimum goal',
    amount: '$6,500',
    translationKey: 'basicCare',
    color: 'from-[#1e3a5f] to-[#2d5a8f]',
    textColor: 'text-white',
    bgColor: 'bg-[#1e3a5f]'
  },
  {
    id: 2,
    type: 'Stretch goal',
    amount: '$15,000',
    translationKey: 'expansion',
    color: 'from-gray-50 to-gray-100',
    textColor: 'text-[#1e3a5f]',
    bgColor: 'bg-white',
    border: 'border-2 border-gray-200'
  },
  {
    id: 3,
    type: 'Scale goal',
    amount: '$27,500',
    translationKey: 'vetCare',
    color: 'from-gray-50 to-gray-100',
    textColor: 'text-[#1e3a5f]',
    bgColor: 'bg-white',
    border: 'border-2 border-gray-200'
  },
  {
    id: 4,
    type: 'Dream goal',
    amount: '$45,000',
    translationKey: 'dreamGoal',
    color: 'from-gray-50 to-gray-100',
    textColor: 'text-[#1e3a5f]',
    bgColor: 'bg-white',
    border: 'border-2 border-gray-200'
  }
];

const FundingGoals = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [currentAmount] = useState(1750); // Current progress amount
  const totalGoal = 45000; // Dream goal as the total
  const overallProgress = (currentAmount / totalGoal) * 100;

  return (
    <div className="py-20 bg-gradient-to-b from-[#FAFAF7] to-white">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <div className="inline-block px-6 py-2 bg-[#F4A460] rounded-full mb-6">
            <span className="font-['Manrope'] font-semibold text-white uppercase tracking-wider text-sm">
              {t('sponsor.fundingTitle')}
            </span>
          </div>
          <h2 className="font-['Outfit'] text-4xl md:text-6xl font-bold text-[#1A1C19] tracking-tight mb-6">
            {t('sponsor.fundingHeading')}
          </h2>
          <p className="font-['Manrope'] text-lg md:text-xl text-[#5E635C] max-w-3xl mx-auto leading-relaxed">
            {t('sponsor.fundingDescription')}
          </p>
        </motion.div>

        {/* Goals Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {goals.map((goal, index) => (
            <motion.div
              key={goal.id}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className={`rounded-3xl p-8 ${goal.bgColor} ${goal.border || ''} shadow-[0_8px_30px_rgba(0,0,0,0.06)] hover:shadow-[0_8px_30px_rgba(0,0,0,0.12)] transition-all duration-300`}
            >
              <div className="mb-4">
                <span className={`font-['Manrope'] text-sm font-semibold ${goal.textColor} ${goal.id === 1 ? 'text-[#F4A460]' : ''}`}>
                  {t(`sponsor.${goal.translationKey}`)}
                </span>
              </div>
              <div className={`font-['Outfit'] text-5xl md:text-6xl font-bold ${goal.textColor} mb-6`}>
                {goal.amount}
              </div>
              <p className={`font-['Manrope'] ${goal.textColor} ${goal.id === 1 ? 'text-white/90' : 'text-[#5E635C]'} leading-relaxed min-h-[80px]`}>
                {t(`sponsor.${goal.translationKey}Desc`)}
              </p>
            </motion.div>
          ))}
        </div>

        {/* Unified Progress Bar */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.5 }}
          className="mb-12 bg-white rounded-3xl p-8 shadow-[0_8px_30px_rgba(0,0,0,0.08)]"
        >
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-4 gap-4">
            <div>
              <h3 className="font-['Outfit'] text-2xl font-bold text-[#1A1C19] mb-1">
                {t('sponsor.progressTitle')}
              </h3>
              <p className="font-['Manrope'] text-[#5E635C]">
                {t('sponsor.progressDesc')}
              </p>
            </div>
            <div className="text-right">
              <div className="font-['Outfit'] text-4xl font-bold text-[#F4A460]">
                ${currentAmount.toLocaleString()}
              </div>
              <p className="font-['Manrope'] text-sm text-[#5E635C]">
                of ${totalGoal.toLocaleString()}
              </p>
            </div>
          </div>
          <div className="w-full h-4 bg-gray-200 rounded-full overflow-hidden">
            <div
              className="h-full bg-gradient-to-r from-[#F4A460] to-[#4A90E2] rounded-full transition-all duration-500"
              style={{ width: `${overallProgress}%` }}
            />
          </div>
        </motion.div>

        {/* CTA Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.6 }}
          className="text-center"
        >
          <button
            onClick={() => navigate('/sponsor')}
            className="inline-flex items-center gap-3 px-8 py-4 bg-[#F4A460] hover:bg-[#E89350] text-white font-['Manrope'] font-semibold rounded-full transition-all duration-300 shadow-lg hover:shadow-xl transform hover:-translate-y-1"
          >
            <span>{t('hero.becomeSponsor')}</span>
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
            </svg>
          </button>
          <p className="font-['Manrope'] text-[#5E635C] italic mt-4">
            {t('sponsor.fundingNote')}
          </p>
        </motion.div>
      </div>
    </div>
  );
};

export default FundingGoals;
